/**
 * TaskBoardContainer Component
 *
 * Main orchestrator component for TaskBoard.
 * Composes TaskBoardHeader, TaskBoardFilters, TaskBoardTable, and Kanban.
 * Manages state and data fetching, delegating rendering to sub-components.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Row, message, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import { debounce } from 'lodash';
import qs from 'qs';
import moment from 'moment';

import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import { useOrgContext } from '../../../../../core/components/orgs/useOrgContext';
import useSearchParams from '../../../../../core/hooks/useSearchParams';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { deduplicateUsers, isRestrictedTaskLevel } from '../../utils/task.utils';
import { ENABLED_USER_FILTER } from '../../../../../core/utils/enabledUserFilter';
import TaskBoardHeader from './TaskBoardHeader';
import TaskBoardFilters from './TaskBoardFilters';
import TaskBoardTable, { TaskTableSkeleton } from './TaskBoardTable';
import Kanban from '../kanban';
import { TaskRecentActivity } from '../TaskRecentActivity';
import TaskDrawerFull from '../TaskDrawerFull';
import { BulkTaskImportModal } from '../modals';
import TaskCreateModal from '../forms/TaskCreateModal';
import useClientOrgFilters from '../../hooks/useClientOrgFilters';
import { BackToTop } from '../shared';
import { DEFAULT_STATUSES } from '../../configs/task.config';
import '../../styles/index.scss';

const DEFAULT_SEARCH_FIELDS = 'name';

/**
 * Build GraphQL variables for task query
 */
const buildTaskQueryVariables = ({
  skip,
  first,
  searchParams,
  showMyTasks,
  projectFilter,
  visibleColumns,
  currentUser,
  isKanban,
  orderBy = 'updatedAt_DESC',
}) => {
  const {
    assigneeNames = '',
    requestor = '',
    requestTypes = '',
    requestSubTypes = '',
    status = '',
    advertiserTypes = '',
    channelPartner = '',
    advertiser = '',
    name = '',
    searchFields = DEFAULT_SEARCH_FIELDS,
    urgency = '',
    dueDate = '',
    showAllStatuses = true,
    teams = '',
  } = searchParams;

  const assigneeNamesArray = assigneeNames ? assigneeNames.split(',').filter(Boolean) : [];
  const teamsArray = teams ? teams.split(',').filter(Boolean) : [];
  const requestorArray = requestor ? requestor.split(',').filter(Boolean) : [];
  const requestTypesArray = requestTypes ? requestTypes.split(',').filter(Boolean) : [];
  const requestSubTypesArray = requestSubTypes ? requestSubTypes.split(',').filter(Boolean) : [];
  const statusArray = status ? status.split(',').filter(Boolean) : [];
  const advertiserTypesArray = advertiserTypes ? advertiserTypes.split(',').filter(Boolean) : [];
  const channelPartnerArray = channelPartner ? channelPartner.split(',').filter(Boolean) : [];
  const advertiserArray = advertiser ? advertiser.split(',').filter(Boolean) : [];
  const urgencyArray = urgency ? urgency.split(',').filter(Boolean) : [];
  const requestedSearchFields = searchFields ? searchFields.split(',').filter(Boolean) : [];
  // Guard: if the user typed a query but unchecked every field, fall back to
  // name-only so the input is never silently inert.
  const effectiveSearchFields = name && requestedSearchFields.length === 0
    ? ['name']
    : requestedSearchFields;

  const searchClauses = name
    ? [
        ...(effectiveSearchFields.includes('name') ? [{ name_contains: name }] : []),
        ...(effectiveSearchFields.includes('description') ? [{ taskDetails_contains: name }] : []),
        ...(effectiveSearchFields.includes('comments')
          ? [{ attachedComments_some: { comment_contains: name } }]
          : []),
      ]
    : [];

  const following = visibleColumns?.includes('following') || false;
  const collaborating = visibleColumns?.includes('collaborating') || false;

  // Parse due date range (comma-separated format: "YYYY-MM-DDTHH:mm:ss,YYYY-MM-DDTHH:mm:ss")
  let dueDateStart = null;
  let dueDateEnd = null;
  if (dueDate && dueDate.split(',').length === 2) {
    [dueDateStart, dueDateEnd] = dueDate.split(',');
  }

  const where = {
    AND: [
      ...(searchClauses.length ? [{ OR: searchClauses }] : []),
      // My tasks filter
      ...(showMyTasks && currentUser?.isPreviewOrg
        ? [{ id: 'no-mytasks-in-preview-mode' }]
        : showMyTasks && currentUser
          ? [
              {
                OR: [
                  { assigneeUser: { id: currentUser.id } },
                  { assigneeUsers_some: { id: currentUser.id } },
                  { createdBy: { id: currentUser.id } },
                  { followers_some: { id: currentUser.id } },
                  { collaborators_some: { id: currentUser.id } },
                ],
              },
            ]
          : []),
      // Assignee filter
      ...(assigneeNamesArray.length
        ? [
            {
              OR: [
                { assigneeUsers_some: { id_in: assigneeNamesArray } },
                { assigneeUser: { id_in: assigneeNamesArray } },
              ],
            },
          ]
        : []),
      // Teams filter
      ...(teamsArray.length ? [{ teams_some: { id_in: teamsArray } }] : []),
      // Requestor filter
      ...(requestorArray.length
        ? [{ createdBy: { id_in: requestorArray } }]
        : []),
      // Request type filter
      ...(requestTypesArray.length ? [{ requestType_in: requestTypesArray }] : []),
      // Request sub type filter
      ...(requestSubTypesArray.length ? [{ requestSubType_in: requestSubTypesArray }] : []),
      // Status filter
      ...(statusArray.length
        ? [{ status_in: statusArray }]
        : !isKanban
          ? showAllStatuses === true || showAllStatuses === 'true'
            ? []
            : [{ status_in: DEFAULT_STATUSES }]
          : []),
      // Advertiser type filter
      ...(advertiserTypesArray.length
        ? [{ createdBy: { roles_some: { org: { level_in: advertiserTypesArray } } } }]
        : []),
      // Channel partner filter - also matches advertisers sitting under the partner, so
      // tasks saved before channelPartner was stored still come back
      ...(channelPartnerArray.length
        ? [
            {
              OR: [
                { channelPartner: { id_in: channelPartnerArray } },
                { advertiser: { parentOrg_some: { id_in: channelPartnerArray } } },
              ],
            },
          ]
        : []),
      // Advertiser filter
      ...(advertiserArray.length ? [{ advertiser: { id_in: advertiserArray } }] : []),
      // Urgency filter
      ...(urgencyArray.length ? [{ urgency_in: urgencyArray }] : []),
      // Due date filter
      ...(dueDateStart && dueDateEnd
        ? [{ dueDate_gte: dueDateStart, dueDate_lte: dueDateEnd }]
        : []),
      // Following/Collaborating filter
      ...(following || collaborating
        ? [
            {
              OR: [
                ...(following && currentUser ? [{ followers_some: { id: currentUser.id } }] : []),
                ...(collaborating && currentUser ? [{ collaborators_some: { id: currentUser.id } }] : []),
              ],
            },
          ]
        : []),
      // Project filter
      ...(projectFilter !== 'all'
        ? projectFilter === 'projectOnly'
          ? [{ projects_some: {} }]
          : [{ projects_none: {} }]
        : []),
      // Org filter - user can only see tasks from their org hierarchy
      ...(currentUser?.role?.org?.id
        ? [
            {
              OR: [
                {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          { parentOrg_some: { id: currentUser.role.org.id } },
                          { id: currentUser.role.org.id },
                        ],
                      },
                    },
                  },
                },
                ...(showMyTasks && currentUser
                  ? [
                      {
                        OR: [
                          { assigneeUser: { id: currentUser.id } },
                          { assigneeUsers_some: { id: currentUser.id } },
                          { followers_some: { id: currentUser.id } },
                          { collaborators_some: { id: currentUser.id } },
                        ],
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ],
  };

  return {
    variables: {
      where,
      skip,
      first,
      orderBy,
    },
  };
};

const TaskBoardContainer = ({ history, location }) => {
  // Context hooks
  const {
    currentUser,
    fetchTasksPaginated,
    task,
    setTask,
    taskBoardPagination,
    setTaskBoardPagination,
    tasksLoading,
    showMyTasks,
    setShowMyTasks,
    isKanban,
    setIsKanban,
    viewChanged,
    setViewChanged,
    fetchOrgTeams,
    orgTeamsData,
    orgTeamsLoading,
  } = useTaskContext();

  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
    fetchRequestorUsers,
    requestorData,
    requestorLoading,
  } = useUserContext();

  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();

  // Window width for responsive behavior
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();

  // User's organization info
  const currentUserOrg = currentUser?.role?.org || {};
  const level = currentUserOrg.level || 'ADVERTISER';

  // Local state
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [projectFilter, setProjectFilter] = useState('all');
  const [bulkImportModalVisible, setBulkImportModalVisible] = useState(false);
  const [createTaskModalVisible, setCreateTaskModalVisible] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState(moment().valueOf());

  // Search params hook
  const [searchParams, setSearchParams] = useSearchParams({
    assigneeNames: '',
    teams: '',
    requestor: '',
    requestTypes: '',
    requestSubTypes: '',
    status: '',
    advertiserTypes: '',
    channelPartner: '',
    advertiser: '',
    name: '',
    searchFields: DEFAULT_SEARCH_FIELDS,
    urgency: '',
    showMyTasks: showMyTasks,
    following: visibleColumns ? visibleColumns.includes('following') : false,
    collaborating: visibleColumns ? visibleColumns.includes('collaborating') : false,
    dueDate: '',
    kanban: isKanban,
    showAllStatuses: true,
    showProjectTasks: false,
  });

  // Unique users for filters
  const uniqueUsers = useMemo(
    () => deduplicateUsers(basicUsersDetails || []),
    [basicUsersDetails]
  );

  const uniqueRequestors = useMemo(
    () => deduplicateUsers(requestorData || []),
    [requestorData]
  );

  // Teams for the Teams filter — fetched via TaskProvider's org-scoped instance (separate from the
  // drawer/form teamsData, so those can't clobber the board's list). Scoped to the user's org.
  useEffect(() => {
    if (currentUserOrg.id) {
      fetchOrgTeams({
        variables: { first: 100, skip: 0, where: { org: { id: currentUserOrg.id } } },
      });
    }
  }, [currentUserOrg.id, fetchOrgTeams]);

  const uniqueTeams = useMemo(
    () =>
      (orgTeamsData?.teams || []).filter(
        (t) => t && t.name && !t.name.startsWith('__deleted__')
      ),
    [orgTeamsData]
  );

  // Channel Partner / Advertiser options come from the same tree the create-task form uses.
  // Non-PLATFORM levels fall back to the flat org fetch — at their level parentOrg_some
  // already resolves to their own advertisers.
  const {
    isPlatform,
    channelPartnerOptions,
    advertiserOptions,
    advertisersUnder,
    loading: clientOrgTreeLoading,
  } = useClientOrgFilters({
    level,
    userOrgId: currentUserOrg.id,
    selectedPartners: searchParams.channelPartner,
    fallbackOrgs: basicOrgsDetails,
  });

  // Filter handlers
  const handleAssigneeFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      assigneeNames: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleTeamsFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      teams: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleRequestorFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestor: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleTaskTypeFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleTaskSubTypeFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestSubTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleStatusFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      status: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleAdvertiserTypeFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiserTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  // Picking partners also drops any selected advertiser that no longer sits under them.
  // Done in one setSearchParams so the board fires a single refetch (no sync-up useEffect).
  const handleChannelPartnerFilter = useCallback((selectedValues) => {
    const partners = selectedValues || [];
    const allowedAdvertisers = partners.length ? advertisersUnder(partners) : null;
    const updatedFilters = {
      ...searchParams,
      channelPartner: partners.join(','),
      advertiser: allowedAdvertisers
        ? (searchParams.advertiser || '')
            .split(',')
            .filter((id) => allowedAdvertisers.includes(id))
            .join(',')
        : searchParams.advertiser || '',
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination, advertisersUnder]);

  const handleAdvertiserFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiser: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleSearchFieldsChange = useCallback((selectedFields) => {
    const updatedFilters = {
      ...searchParams,
      searchFields: (selectedFields || []).join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleUrgencyFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      urgency: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  const handleDueDateFilter = useCallback((selectedValue) => {
    let dueDateValue = selectedValue || '';
    // Always set start to T00:00:00 and end to T23:59:59 if a range is selected
    if (dueDateValue && dueDateValue.split && dueDateValue.split(',').length === 2) {
      let [from, to] = dueDateValue.split(',');
      from = from.split('T')[0];
      to = to.split('T')[0];
      dueDateValue = `${from}T00:00:00,${to}T23:59:59`;
    }
    const updatedFilters = {
      ...searchParams,
      dueDate: dueDateValue,
    };
    setSearchParams(updatedFilters);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setTaskBoardPagination]);

  // Local state for instant input feedback; debounce only the API/URL update
  const [localSearchValue, setLocalSearchValue] = useState(searchParams.name || '');
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const debouncedSearchUpdate = useMemo(
    () =>
      debounce((value) => {
        setSearchParamsRef.current({ ...searchParamsRef.current, name: value });
        setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
      }, 300),
    [setTaskBoardPagination]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setLocalSearchValue(value);
      debouncedSearchUpdate(value);
    },
    [debouncedSearchUpdate]
  );

  useEffect(() => () => debouncedSearchUpdate.cancel(), [debouncedSearchUpdate]);

  // Sync the search bar ALWAYS
  useEffect(() => {
    setLocalSearchValue(searchParams.name || '');
  }, [searchParams.name]);

  // Fetch users for filters - org-scoped query with task assignment filtering
  const fetchBasicUsers = useCallback(
    debounce((name = '') => {
      fetchBasicUsersDetails({
        variables: {
          first: 200,
          skip: 0,
          where: {
            AND: [
              {
                assignedUserTasks_some: {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          { id: currentUser?.role?.org?.id },
                          { parentOrg_some: { id: currentUser?.role?.org?.id } },
                        ],
                      },
                    },
                  },
                },
              },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
              ENABLED_USER_FILTER,
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser?.role?.org?.id, fetchBasicUsersDetails]
  );

  // Fetch requestors for filters - org-scoped query with task creation filtering
  const fetchBasicUsersRequestor = useCallback(
    debounce((name = '') => {
      fetchRequestorUsers({
        variables: {
          first: 200,
          skip: 0,
          where: {
            AND: [
              {
                createdTasks_some: {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          { id: currentUser?.role?.org?.id },
                          { parentOrg_some: { id: currentUser?.role?.org?.id } },
                        ],
                      },
                    },
                  },
                },
              },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
              ENABLED_USER_FILTER,
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser?.role?.org?.id, fetchRequestorUsers]
  );

  // Fetch advertiser orgs for filters - all orgs within the logged-in org hierarchy
  const fetchAdvertiserOrgs = useCallback(
    debounce((name = '') => {
      fetchBasicOrgsDetails({
        variables: {
          first: 10000,
          skip: 0,
          where: {
            AND: [
              {
                OR: [
                  { id: currentUser?.role?.org?.id },
                  { parentOrg_some: { id: currentUser?.role?.org?.id } },
                ],
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser?.role?.org?.id, fetchBasicOrgsDetails]
  );

  // Initial data fetch
  useEffect(() => {
    fetchBasicUsers('');
    fetchBasicUsersRequestor('');
    fetchAdvertiserOrgs('');
  }, [fetchAdvertiserOrgs, fetchBasicUsers, fetchBasicUsersRequestor]);

  // Auto-load saved filters when user logs in (once per session only)
  useEffect(() => {
    if (!currentUser || !currentUser.id) return;

    const sessionFlagKey = `taskStateAutoLoaded_${currentUser.id}`;
    if (sessionStorage.getItem(sessionFlagKey)) return;

    const savedState = localStorage.getItem(`taskState_${currentUser.id}`);
    if (!savedState) {
      sessionStorage.setItem(sessionFlagKey, '1');
      return;
    }

    try {
      const {
        searchParams: savedSearchParams,
        taskBoardPagination: savedPagination,
        isKanban: savedIsKanban,
        visibleColumns: savedVisibleColumns,
        projectFilter: savedProjectFilter,
      } = JSON.parse(savedState);

      setSearchParams(savedSearchParams);
      setTaskBoardPagination(savedPagination);

      if (typeof savedIsKanban === 'boolean') {
        setIsKanban(savedIsKanban);
      }

      if (Array.isArray(savedVisibleColumns)) {
        setVisibleColumns(savedVisibleColumns);
      }

      if (savedProjectFilter) {
        setProjectFilter(savedProjectFilter);
      }

      const queryString = qs.stringify(savedSearchParams, { addQueryPrefix: true });
      history.push(`/tasks/main/${savedPagination.current}${queryString}`);
    } catch (error) {
      // Failed to load saved filters - use defaults
    } finally {
      sessionStorage.setItem(sessionFlagKey, '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  // Clear tasks immediately when view changes (My Tasks ↔ All Tasks)
  useEffect(() => {
    if (viewChanged) {
      setTask([]);
      setViewChanged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewChanged]);

  // Fetch tasks on filter/view changes
  // Kanban needs more tasks (up to 500), table uses pagination pageSize
  const KANBAN_FETCH_LIMIT = 500;

  useEffect(() => {
    if (isKanban) {
      fetchTasksPaginated(
        buildTaskQueryVariables({
          skip: 0,
          first: KANBAN_FETCH_LIMIT,
          searchParams,
          showMyTasks,
          projectFilter,
          visibleColumns,
          currentUser,
          isKanban,
          orderBy: 'updatedAt_DESC',
        })
      );
    } else {
      const newPagination = {
        ...taskBoardPagination,
        current: 1,
      };
      setTaskBoardPagination(newPagination);

      fetchTasksPaginated(
        buildTaskQueryVariables({
          skip: 0,
          first: newPagination.pageSize,
          searchParams,
          showMyTasks,
          projectFilter,
          visibleColumns,
          currentUser,
          isKanban,
          orderBy: 'updatedAt_DESC',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKanban, showMyTasks, searchParams, projectFilter, visibleColumns]);

  // Event handlers
  const handleTaskClick = useCallback((taskId) => {
    setSelectedTaskId(taskId);
    setDrawerVisible(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    setSelectedTaskId(null);
  }, []);

  const handleTaskUpdate = useCallback(() => {
    if (isKanban) {
      fetchTasksPaginated(
        buildTaskQueryVariables({
          skip: 0,
          first: KANBAN_FETCH_LIMIT,
          searchParams,
          showMyTasks,
          projectFilter,
          visibleColumns,
          currentUser,
          isKanban,
          orderBy: 'updatedAt_DESC',
        })
      );
    } else {
      fetchTasksPaginated(
        buildTaskQueryVariables({
          skip: (taskBoardPagination.current - 1) * taskBoardPagination.pageSize || 0,
          first: taskBoardPagination.pageSize,
          searchParams,
          showMyTasks,
          projectFilter,
          visibleColumns,
          currentUser,
          isKanban,
          orderBy: 'updatedAt_DESC',
        })
      );
    }
  }, [isKanban, searchParams, showMyTasks, projectFilter, visibleColumns, taskBoardPagination, fetchTasksPaginated, currentUser]);

  const handlePaginationChange = useCallback(
    (params) => {
      if (isKanban) return;
      const updatedPagination = {
        ...taskBoardPagination,
        current: params.page || taskBoardPagination.current,
        pageSize: params.pageSize || taskBoardPagination.pageSize,
      };

      const sortOrder =
        params.sortOrder === 'ascend'
          ? 'ASC'
          : params.sortOrder === 'descend'
            ? 'DESC'
            : 'updatedAt_DESC';

      const sortField =
        sortOrder === 'updatedAt_DESC' ? null : params.sortField || taskBoardPagination.sortField;

      setTaskBoardPagination(updatedPagination);

      fetchTasksPaginated(
        buildTaskQueryVariables({
          skip: (updatedPagination.current - 1) * updatedPagination.pageSize || 0,
          first: updatedPagination.pageSize,
          searchParams,
          showMyTasks,
          projectFilter,
          visibleColumns,
          currentUser,
          isKanban,
          orderBy: sortOrder === 'updatedAt_DESC'
            ? 'updatedAt_DESC'
            : sortField
              ? `${sortField}_${sortOrder}`
              : 'updatedAt_DESC',
        })
      );
    },
    [taskBoardPagination, searchParams, showMyTasks, projectFilter, visibleColumns, fetchTasksPaginated, setTaskBoardPagination, currentUser, isKanban]
  );

  const handleTableChange = useCallback(
    (pagination, filters, sorter) => {
      if (isKanban) return;
      handlePaginationChange({
        page: 1,
        sortField: sorter.field || null,
        sortOrder: sorter.order || null,
      });
    },
    [isKanban, handlePaginationChange]
  );

  const handlePaginationSizeChange = useCallback(
    (current, size) => {
      if (isKanban) return;
      handlePaginationChange({
        page: 1,
        pageSize: size,
      });
    },
    [isKanban, handlePaginationChange]
  );

  const handleClearFilters = useCallback(() => {
    const clearedParams = {
      assigneeNames: '',
      teams: '',
      requestor: '',
      requestTypes: '',
      requestSubTypes: '',
      status: '',
      advertiserTypes: '',
      channelPartner: '',
      advertiser: '',
      name: '',
      urgency: '',
      dueDate: '',
      following: false,
      collaborating: false,
      showAllStatuses: false,
    };
    setSearchParams(clearedParams);
    setVisibleColumns([]);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
    fetchTasksPaginated(
      buildTaskQueryVariables({
        skip: 0,
        first: taskBoardPagination.pageSize,
        searchParams: clearedParams,
        showMyTasks,
        projectFilter,
        visibleColumns: [],
        currentUser,
        isKanban,
        orderBy: 'updatedAt_DESC',
      })
    );
  }, [taskBoardPagination, showMyTasks, projectFilter, fetchTasksPaginated, setSearchParams, setTaskBoardPagination, currentUser, isKanban]);

  // Check if filters have changed from saved state
  const hasFilterChanges = useMemo(() => {
    if (!currentUser?.id) return true;
    const savedState = localStorage.getItem(`taskState_${currentUser.id}`);
    if (!savedState) return true;

    try {
      const {
        searchParams: savedSearchParams,
        taskBoardPagination: savedPagination,
        isKanban: savedIsKanban,
        visibleColumns: savedVisibleColumns,
      } = JSON.parse(savedState);

      const currentState = { searchParams, taskBoardPagination, isKanban, visibleColumns };
      const savedStateObj = {
        searchParams: savedSearchParams,
        taskBoardPagination: savedPagination,
        isKanban: savedIsKanban,
        visibleColumns: savedVisibleColumns,
      };

      return JSON.stringify(currentState) !== JSON.stringify(savedStateObj);
    } catch (error) {
      return true;
    }
  }, [currentUser.id, searchParams, taskBoardPagination, isKanban, visibleColumns]);

  const handleSaveFilters = useCallback(() => {
    if (!currentUser?.id) {
      message.error('Unable to save filters');
      return;
    }

    if (!hasFilterChanges) {
      message.destroy();
      message.info('No filter changes detected.');
      return;
    }

    // Save all state including Kanban preference per user
    const stateToSave = {
      searchParams: { ...searchParams },
      taskBoardPagination,
      isKanban,
      visibleColumns,
      projectFilter,
    };

    localStorage.setItem(`taskState_${currentUser.id}`, JSON.stringify(stateToSave));
    setLastSavedTimestamp(moment().valueOf());
    message.destroy();
    message.success('Current search and filters saved successfully!');
  }, [currentUser.id, hasFilterChanges, searchParams, taskBoardPagination, isKanban, visibleColumns, projectFilter]);

  const handleLoadFilters = useCallback(() => {
    if (!currentUser?.id) {
      message.error('Unable to load filters');
      return;
    }

    const savedState = localStorage.getItem(`taskState_${currentUser.id}`);
    if (!savedState) {
      message.destroy();
      message.info('No saved filters found.');
      return;
    }

    try {
      const {
        searchParams: savedSearchParams,
        taskBoardPagination: savedPagination,
        isKanban: savedIsKanban,
        visibleColumns: savedVisibleColumns,
        projectFilter: savedProjectFilter,
      } = JSON.parse(savedState);

      setSearchParams(savedSearchParams);
      setTaskBoardPagination(savedPagination);

      // Restore Kanban preference if saved
      if (typeof savedIsKanban === 'boolean') {
        setIsKanban(savedIsKanban);
      }

      if (Array.isArray(savedVisibleColumns)) {
        setVisibleColumns(savedVisibleColumns);
      }

      if (savedProjectFilter) {
        setProjectFilter(savedProjectFilter);
      }

      const queryString = qs.stringify(savedSearchParams, { addQueryPrefix: true });
      history.push(`/tasks/main/${savedPagination.current}${queryString}`);

      message.destroy();
      message.success('Filters loaded successfully!');
    } catch (error) {
      message.error('Failed to load saved filters.');
    }
  }, [currentUser.id, setSearchParams, setTaskBoardPagination, history, setIsKanban]);

  const handlePaginationReset = useCallback(() => {
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
  }, [setTaskBoardPagination]);

  const handleBulkImportSuccess = useCallback(() => {
    setBulkImportModalVisible(false);
    setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
    fetchTasksPaginated(
      buildTaskQueryVariables({
        skip: 0,
        first: taskBoardPagination.pageSize,
        searchParams,
        showMyTasks,
        projectFilter,
        visibleColumns,
        currentUser,
        isKanban,
        orderBy: 'updatedAt_DESC',
      })
    );
    const queryString = qs.stringify(searchParams, { addQueryPrefix: true });
    history.push(`/tasks/main/1${queryString}`);
  }, [searchParams, taskBoardPagination, fetchTasksPaginated, history, setTaskBoardPagination, showMyTasks, projectFilter, visibleColumns, currentUser, isKanban]);

  // Check if any filters are active
  const hasActiveFilters = Object.values(searchParams).some(
    (param) => param && param !== 'false' && param !== false
  );

  // Calculate active filter count for header badge (Option B)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchParams.assigneeNames) count++;
    if (searchParams.teams) count++;
    if (searchParams.requestor) count++;
    if (searchParams.requestTypes) count++;
    if (searchParams.requestSubTypes) count++;
    if (searchParams.status) count++;
    if (searchParams.urgency) count++;
    if (searchParams.dueDate) count++;
    if (searchParams.channelPartner) count++;
    if (searchParams.advertiser) count++;
    if (searchParams.advertiserTypes) count++;
    return count;
  }, [searchParams]);

  return (
    <>
      {/* Preview Mode Banner */}
      {showMyTasks && currentUser?.isPreviewOrg && (
        <div className="task-board__warning-banner">
          <span className="task-board__warning-banner__emphasis">Preview Mode Enabled!</span>
          No user 'My Tasks' data is shown. Please switch to All Tasks or Disable the Preview Mode.
        </div>
      )}

      {/* Recent Activity */}
      <TaskRecentActivity />

      {/* Header */}
      <TaskBoardHeader
        windowWidth={windowWidth}
        showMyTasks={showMyTasks}
        setShowMyTasks={(value) => {
          setShowMyTasks(value);
          setVisibleColumns([]);
          setSearchParams({ ...searchParams, following: false, collaborating: false });
          setViewChanged(true);
          setTaskBoardPagination((prev) => ({ ...prev, current: 1 }));
        }}
        searchValue={localSearchValue}
        onSearchChange={handleSearchChange}
        onSearchClear={() => {
          setLocalSearchValue('');
          debouncedSearchUpdate.cancel();
          setSearchParams({ ...searchParams, name: '' });
        }}
        searchFields={
          (searchParams.searchFields ?? DEFAULT_SEARCH_FIELDS)
            .split(',')
            .filter(Boolean)
        }
        onSearchFieldsChange={handleSearchFieldsChange}
        onBulkImportClick={() => setBulkImportModalVisible(true)}
        onCreateTaskClick={() => setCreateTaskModalVisible(true)}
        level={level}
        setViewChanged={setViewChanged}
        // Filter integration props
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        activeFilterCount={activeFilterCount}
        // Export where clause matching the board's exact query
        getExportWhere={() => buildTaskQueryVariables({
          skip: 0,
          first: 10000,
          searchParams,
          showMyTasks,
          projectFilter,
          visibleColumns,
          currentUser,
          isKanban,
          orderBy: 'updatedAt_DESC',
        }).variables.where}
      />

      {/* Filters */}
      <TaskBoardFilters
        windowWidth={windowWidth}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        level={level}
        showMyTasks={showMyTasks}
        isKanban={isKanban}
        // Filter handlers
        handleAssigneeFilter={handleAssigneeFilter}
        handleTeamsFilter={handleTeamsFilter}
        handleRequestorFilter={handleRequestorFilter}
        handleTaskTypeFilter={handleTaskTypeFilter}
        handleTaskSubTypeFilter={handleTaskSubTypeFilter}
        handleStatusFilter={handleStatusFilter}
        handleAdvertiserTypeFilter={handleAdvertiserTypeFilter}
        handleChannelPartnerFilter={handleChannelPartnerFilter}
        handleAdvertiserFilter={handleAdvertiserFilter}
        handleUrgencyFilter={handleUrgencyFilter}
        handleDueDateFilter={handleDueDateFilter}
        // User/Org data
        uniqueUsers={uniqueUsers}
        uniqueTeams={uniqueTeams}
        uniqueRequestors={uniqueRequestors}
        channelPartnerOptions={channelPartnerOptions}
        advertiserOptions={advertiserOptions}
        // Loading states
        basicUsersDetailsLoading={basicUsersDetailsLoading}
        teamsLoading={orgTeamsLoading}
        requestorLoading={requestorLoading}
        channelPartnerLoading={clientOrgTreeLoading}
        advertiserLoading={isPlatform ? clientOrgTreeLoading : basicOrgsDetailsLoading}
        // Fetch functions
        fetchBasicUsers={fetchBasicUsers}
        fetchBasicUsersRequestor={fetchBasicUsersRequestor}
        fetchAdvertiserOrgs={fetchAdvertiserOrgs}
        // Filter actions
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        projectFilter={projectFilter}
        setProjectFilter={setProjectFilter}
        onClearFilters={handleClearFilters}
        onSaveFilters={handleSaveFilters}
        onLoadFilters={handleLoadFilters}
        hasActiveFilters={hasActiveFilters}
        onPaginationReset={handlePaginationReset}
      />

      {/* Content: Kanban or Table */}
      {isKanban ? (
        <>
          {taskBoardPagination.total > 500 && (
            <div className="task-board__warning-banner">
              <span className="task-board__warning-banner__emphasis">Note:</span>
              Showing first 500 of {taskBoardPagination.total} tasks. Use filters to narrow down
              results.
            </div>
          )}
          <Kanban tasks={Array.isArray(task) ? task : []} showAllTasks={!showMyTasks} />
        </>
      ) : (
        <>
          {tasksLoading && (!task || task.length === 0) ? (
            <div className="task-board__table-wrapper">
              <TaskTableSkeleton
                rows={taskBoardPagination.pageSize > 10 ? 10 : taskBoardPagination.pageSize}
                isMobile={isMobile}
                isTablet={!isMobile && !isDesktop}
                showAdvertiser={!isRestrictedTaskLevel(level)}
              />
            </div>
          ) : (
            <TaskBoardTable
              tasks={task}
              loading={tasksLoading}
              pagination={taskBoardPagination}
              windowWidth={windowWidth}
              level={level}
              currentUser={currentUser}
              onTaskClick={handleTaskClick}
              onTableChange={handleTableChange}
              onPaginationChange={handlePaginationChange}
              onPaginationSizeChange={handlePaginationSizeChange}
            />
          )}
        </>
      )}

      {/* Task Drawer */}
      <TaskDrawerFull
        visible={drawerVisible}
        onClose={handleDrawerClose}
        taskId={selectedTaskId}
        onTaskUpdate={handleTaskUpdate}
      />

      {/* Bulk Import Modal */}
      <BulkTaskImportModal
        visible={bulkImportModalVisible}
        onClose={() => setBulkImportModalVisible(false)}
        onSuccess={handleBulkImportSuccess}
      />

      {/* Create Task Modal (kept mounted so the open/close fade plays; afterClose resets it) */}
      <TaskCreateModal
        visible={createTaskModalVisible}
        onClose={() => setCreateTaskModalVisible(false)}
        onSuccess={handleTaskUpdate}
      />

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          className="task-board__fab"
          onClick={() => setCreateTaskModalVisible(true)}
          aria-label="Create new task"
        />
      )}

      <BackToTop />
    </>
  );
};

TaskBoardContainer.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default withRouter(TaskBoardContainer);
